import { Stack, Typography } from '@mui/material';
import { NodeChange } from '@xyflow/react';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Node, TileNode } from '@/components/diagrams/FlowChart';
import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { SidePanel } from '@/components/modals/SidePanel';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { PopperContent } from '@/components/poppers/PopperContent';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { PathsOf } from '@/types/subform';

import { useEntityTileOptions } from './hooks/useEntityTileOptions';
import { useGenerateTileNotes } from './hooks/useGenerateTileNotes';
import { useTileNotesGenerationTarget } from './hooks/useTileNotesGenerationTarget';
import {
  getSelectedTileIdsFromNodes,
  useTileSelectionSync,
} from './hooks/useTileSelectionSync';
import { TileNotesLoadingOverlay } from './TileNotesLoadingOverlay';
import {
  TILE_NOTES_PROMPT_OPTIONS,
  TILE_NOTES_PROMPT_TEXTS,
  TileNotesPromptId,
} from './types';

interface EntityTileNotesSummaryPanelFormShape {
  tileIds: string[];
  promptId: TileNotesPromptId;
  promptText: string;
  matchSummaryFormats: boolean;
}

type EntityTileNotesSummaryPanelFormShapePaths =
  PathsOf<EntityTileNotesSummaryPanelFormShape>;

export interface EntityTileNotesSummaryPanelProps {
  /** Current diagram nodes, used to populate and synchronize tile selection. */
  nodes: Node[];
  /** Updates selection state on the current diagram. */
  dispatch: (action: {
    type: 'NODES_CHANGED';
    nodes: Node[];
    changes: NodeChange[];
    isInteractive?: boolean;
  }) => void;
  /** Applies generated notes (keyed by tile/node id) back onto the diagram. */
  onApplyNotes: (updates: Record<string, string>) => void;
  onClose: () => void;
}

/**
 * Bulk AI tile-notes panel for the single-entity / ownership diagram and the
 * household entity map. Mirrors the waterfall's TileNotesSummaryPanel, but
 * supports editing the diagram selection in-panel and applies notes through a
 * surface-provided callback. Which surface is active — and how
 * a tile maps to a TileNotesInput — is resolved by useTileNotesGenerationTarget.
 */
export function EntityTileNotesSummaryPanel({
  nodes,
  dispatch,
  onApplyNotes,
  onClose,
}: EntityTileNotesSummaryPanelProps) {
  const target = useTileNotesGenerationTarget();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const [tileSearchText, setTileSearchText] = useState('');

  const { generate, isGenerating, currentStreamingNote } = useGenerateTileNotes(
    {
      onSuccess: onApplyNotes,
    }
  );

  const formMethods = useForm<EntityTileNotesSummaryPanelFormShape>({
    defaultValues: {
      tileIds: getSelectedTileIdsFromNodes(nodes),
      promptId: 'trustees-access',
      promptText: '',
      matchSummaryFormats: false,
    },
  });

  const { control, handleSubmit, setValue } = formMethods;

  const { selectedTileIds } = useTileSelectionSync({
    control,
    fieldName: 'tileIds',
    setValue,
    nodes,
    dispatch,
  });

  const tileOptions = useEntityTileOptions({
    tileNodes: nodes.filter((node): node is TileNode => node.type === 'tile'),
    target,
  });

  // The target's per-tile builder is the single source of truth for "can this
  // tile generate notes"; it drops individuals, organizations, and unsupported
  // entity kinds. An empty result means the whole selection is unsupported.
  const generatableInputs = useMemo(
    () =>
      target
        ? selectedTileIds.flatMap((tileId) => target.buildInputForTile(tileId))
        : [],
    [target, selectedTileIds]
  );
  const supportedCount = generatableInputs.length;
  const hasUnsupportedTiles = supportedCount < selectedTileIds.length;

  const promptId = useWatch({ control, name: 'promptId' });
  useEffect(() => {
    if (promptId === 'custom') {
      setValue('promptText', '');
    } else if (promptId && TILE_NOTES_PROMPT_TEXTS[promptId]) {
      setValue('promptText', TILE_NOTES_PROMPT_TEXTS[promptId] ?? '');
    }
  }, [promptId, setValue]);

  const onGenerate = handleSubmit((data) => {
    if (!target) return;

    if (!data.promptText.trim()) {
      showFeedback('Please enter a prompt');
      return;
    }

    if (generatableInputs.length === 0) {
      showFeedback('No valid tiles selected');
      return;
    }

    trackUserEvent(target.trackingEvent, {
      source: 'EntityTileNotesSummaryPanel',
      tileCount: generatableInputs.length,
      promptId: data.promptId,
      ...target.trackingProps,
    });

    generate(generatableInputs, data.promptText, data.matchSummaryFormats);
  });

  return (
    <FormProvider {...formMethods}>
      {/* relative for this panel to tell the overlay that it should only cover this panel */}
      <SidePanel.Panel style={{ position: 'relative' }}>
        <TileNotesLoadingOverlay
          open={isGenerating}
          currentTileNote={currentStreamingNote}
        />
        <SidePanel.Header title="Tile notes" onClose={onClose} />
        <SidePanel.Content>
          <SidePanel.Section>
            <Typography variant="body-xs">
              Generate custom tile notes for trusts, sub-trusts, and business
              entities with Luminary AI. Existing tile notes will be overridden.
            </Typography>

            <FormAwareTypeaheadMultiSelectInput<EntityTileNotesSummaryPanelFormShape>
              control={control}
              fieldName={
                'tileIds' as const satisfies EntityTileNotesSummaryPanelFormShapePaths
              }
              label="Selected tiles"
              required
              options={tileOptions}
              inputValue={tileSearchText}
              onInputChange={setTileSearchText}
              placeholder="Select tiles"
            />

            {hasUnsupportedTiles && (
              <Callout severity="warning">
                Some selected tiles contain entity types that are not yet
                supported for note generation. Notes will only be generated for
                supported entity types.
              </Callout>
            )}

            <FormAwareSelectInput<EntityTileNotesSummaryPanelFormShape>
              control={control}
              fieldName={
                'promptId' as const satisfies EntityTileNotesSummaryPanelFormShapePaths
              }
              label="Select prompt"
              required
              options={TILE_NOTES_PROMPT_OPTIONS}
              showEmptyValue={false}
            />

            <FormAwareTextInput<EntityTileNotesSummaryPanelFormShape>
              control={control}
              fieldName={
                'promptText' as const satisfies EntityTileNotesSummaryPanelFormShapePaths
              }
              label="Prompt"
              required
              multiline
              rows={6}
              placeholder="Describe the notes you would like to see added to the selected tiles"
            />

            {supportedCount > 1 && (
              <FormAwareCheckbox<EntityTileNotesSummaryPanelFormShape>
                control={control}
                fieldName={
                  'matchSummaryFormats' as const satisfies EntityTileNotesSummaryPanelFormShapePaths
                }
                label="Standardize notes across selected tiles"
                contextualHelp={
                  <PopperContent
                    sx={{
                      maxWidth: '200px',
                    }}
                    body="The content and style of the notes will be standardized for your selected tiles"
                  />
                }
              />
            )}
          </SidePanel.Section>
        </SidePanel.Content>
        <SidePanel.Footer>
          <Stack
            width="100%"
            spacing={1}
            direction="row"
            justifyContent="flex-end"
          >
            <Button
              variant="primary"
              size="sm"
              startIcon={Stars01Icon}
              iconProps={{ size: 16 }}
              onClick={onGenerate}
              disabled={supportedCount === 0 || isGenerating}
              loading={isGenerating}
            >
              Generate notes
            </Button>
          </Stack>
        </SidePanel.Footer>
      </SidePanel.Panel>
    </FormProvider>
  );
}
